import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../Layout';
import { showToast } from '../../helpers/customToast';
import { verifyPaymentStatus } from '../../helpers/stripe';
import { createSwiklyDeposit } from '../../helpers/swikly';
import { LoisirManager } from '../../helpers/loisir';

const LoisirPaymentCompletePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const verificationAttempted = useRef(false);

    useEffect(() => {
        const verifyPayment = async () => {
            // Skip if verification was already attempted
            if (verificationAttempted.current) {
                return;
            }
            verificationAttempted.current = true;

            try {
                const documentId = searchParams.get('document_id');
                const sessionId = searchParams.get('session_id');
                const paymentType = searchParams.get('type') || 'regular';

                if (!documentId || !sessionId) {
                    throw new Error('Missing required parameters');
                }

                // Get formula order details by documentId first
                const order = await LoisirManager.getFormulaOrderByDocumentId(documentId);
                if (!order) {
                    throw new Error('Order not found');
                }

                // Verify payment status
                const paymentStatus = await verifyPaymentStatus(sessionId, documentId, paymentType);
                
                if (paymentStatus.success) {
                    let swiklyResponse;
                    
                    // Only create Swikly deposit if it doesn't exist
                    if (!order.swikly_request_id && order.deposit_amount > 0) {
                        swiklyResponse = await createSwiklyDeposit({
                            orderId: order.id,
                            depositAmount: order.deposit_amount,
                            customerEmail: order.user.email,
                            firstName: order.user.first_name,
                            lastName: order.user.last_name,
                            startDate: order.start_date,
                            endDate: order.end_date
                        });
                    }
                    
                    // Redirect to loisir order confirmation
                    navigate('/loisirs/confirmation', {
                        state: { 
                            orderId: order.id,
                            documentId: documentId,
                            paymentConfirmed: true,
                            swiklyLink: swiklyResponse?.link || order.swikly_url || null
                        }
                    });
                } else {
                    throw new Error('Payment verification failed');
                }
            } catch (error) {
                console.error('Payment verification error:', error);
                showToast('Une erreur est survenue lors de la vérification de votre paiement. Veuillez contacter le support.', 'error');
            } finally {
                setLoading(false);
                // Clear loisir selection from session storage
                LoisirManager.clearSelection();
            }
        };

        verifyPayment();
    }, [searchParams, navigate]);

    if (loading) {
        return (
            <Layout>
                <div className="container my-5">
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Vérification du paiement...</span>
                        </div>
                        <p className="mt-3">Vérification de votre paiement...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return null;
};

export default LoisirPaymentCompletePage;